import url from 'url';
import get from 'lodash/get';
import join from 'lodash/join';
import map from 'lodash/map';
import omitBy from 'lodash/omitBy';
import { MediumTypes } from '../../js/utils/constants';
import { v4 as uuidv4 } from 'uuid';
import { ADSWIZZ_GDPR_PARAMETER, ADSWIZZ_GDPR_TCFV2_TCSTRING_PARAMETER, ADSWIZZ_LOTAME_PARAMETER, ADSWIZZ_REQUEST_URL, DFP_GDPR_NON_PERSONALIZED_ADS_PARAMETER, MIDROLL_SLOT_NAME, PREROLL_SLOT_NAME, US_RESTRICTED_DATA_PROCESSING_ADS_PARAMETER, } from '../ads/constants';
import audioVideoAdController from '../ads/controllers/audioVideoAdController';
import { adPixelUnitSize, videoAdUnitSizes } from '../ads/media/mediaConstants';
import { adsWizzAdParameters } from './adsParameters';
import { State, getState, updateState } from './applicationState';
import targetBuilder from './targetBuilder';
function objectToQueryString(object) {
    return join(map(object, (value, key) => `${key}=${value}`), '&');
}
function filterFalsyValues(object) {
    return omitBy(object, (value) => value === undefined || value === null || value === '');
}
const networkUrlBuilder = {
    buildAdswizzUrl(partnerId, usPrivacyString, isUserInUS, isOptedOutOfTargeting, enableDoublePreroll, isFirstInSession) {
        const adswizzUrl = new URL(ADSWIZZ_REQUEST_URL);
        const targetingInfo = targetBuilder.build(adsWizzAdParameters);
        if (targetingInfo[ADSWIZZ_LOTAME_PARAMETER]) {
            targetingInfo[ADSWIZZ_LOTAME_PARAMETER] = `;${targetingInfo[ADSWIZZ_LOTAME_PARAMETER].join(';')};`;
        }
        updateState(State.adTypeRequested, MediumTypes.audio);
        const requestParams = {
            ...targetingInfo,
            'aw_0_1st.companion_zone_alias': 'TI_Display_300x250_Preroll,TI_Display_728x90_Preroll',
            'aw_0_1st.companionAds': true,
            'aw_0_1st.skey': '2112133398',
            'aw_0_1st.playerid': partnerId,
            'aw_0_1st.ppid': getState(State.ppid),
            'aw_0_1st.maxAds': enableDoublePreroll ? 2 : 1,
            'aw_0_1st.isFirstInSession': isFirstInSession,
        };
        const { gdprApplies, tcString } = getState(State.gdprData);
        if (gdprApplies) {
            requestParams[ADSWIZZ_GDPR_TCFV2_TCSTRING_PARAMETER] = tcString;
            requestParams[ADSWIZZ_GDPR_PARAMETER] = getState(State.canShowPersonalizedAds);
        }
        if (usPrivacyString) {
            requestParams.us_privacy = usPrivacyString;
        }
        if (isUserInUS) {
            requestParams[US_RESTRICTED_DATA_PROCESSING_ADS_PARAMETER] =
                isOptedOutOfTargeting ? 1 : 0;
        }
        adswizzUrl.query = filterFalsyValues(requestParams);
        return url.format(adswizzUrl);
    },
    buildTargetingParams() {
        const targetingInfo = targetBuilder.build();
        return objectToQueryString(targetingInfo);
    },
    determineAdType(canAttemptVideo, isVideoOnly) {
        if (!canAttemptVideo) {
            return 'audio';
        }
        if (isVideoOnly) {
            return 'video';
        }
        return 'audio_video';
    },
    async getInstreamCustomParams() {
        await audioVideoAdController.waitForAdsWizz();
        let targetingParams = this.buildTargetingParams().concat('&audioEnabled=true');
        const adswizzListenerId = window.com_adswizz_synchro_getListenerId?.();
        if (adswizzListenerId) {
            targetingParams += `&listenerId=${adswizzListenerId}`;
        }
        if (targetingParams.lotamesegments) {
            targetingParams.lotamesegments = targetingParams.lotamesegments.join(',');
        }
        return encodeURIComponent(targetingParams);
    },
    buildDfpUrl({ adUnitId, appendPartnerAliasToAdUnitId, canAttemptVideo, usPrivacyString, slotName, isUserInUS, isOptedOutOfTargeting, enableDoublePreroll, isVideoOnly, apsCustParams, isFirstInSession, }) {
        const dfpUrl = new URL('https://pubads.g.doubleclick.net/gampad/ads');
        const targetingParams = this.buildTargetingParams();
        const videoEnabledParams = canAttemptVideo
            ? '&videoEnabled=true'
            : '&videoEnabled=false';
        const audioEnabledParams = isVideoOnly
            ? '&audioEnabled=false'
            : '&audioEnabled=true';
        const { gdprApplies, tcString } = getState(State.gdprData);
        const adType = this.determineAdType(canAttemptVideo, isVideoOnly);
        const volume = getState(State.volume);
        const custParams = targetingParams
            .concat(audioEnabledParams)
            .concat(videoEnabledParams)
            .concat(slotName === PREROLL_SLOT_NAME
            ? `&enableDoublePreroll=${enableDoublePreroll}`
            : '')
            .concat(isVideoOnly && slotName === PREROLL_SLOT_NAME
            ? apsCustParams || ''
            : '');
        const partnerAlias = get(getState(State.config), 'targeting.ads_partner_alias');
        const isVmapPrerollEnabled = getState(State.isVmapPrerollEnabled);
        updateState(State.adTypeRequested, adType);
        let requestParams;
        if (isVmapPrerollEnabled || slotName === MIDROLL_SLOT_NAME) {
            requestParams = {
                ad_rule: 1,
                ad_type: adType,
                ciu_szs: '728x90,300x250,300x600',
                correlator: Date.now(),
                cust_params: custParams,
                env: 'instream',
                gdfp_req: '1',
                plcmt: '1',
                iu: appendPartnerAliasToAdUnitId
                    ? `${adUnitId}${partnerAlias}`
                    : adUnitId,
                output: 'vmap',
                pp: getState(State.isMobile) ? 'Mweb' : 'web',
                ppid: getState(State.ppid),
                sid: uuidv4().toString(),
                sz: canAttemptVideo ? videoAdUnitSizes : adPixelUnitSize,
                unviewed_position_start: '1',
                vad_type: 'linear',
                vpa: 'auto',
                videoad_skippable: '0',
                wta: 0,
                ...getState(State.dfpParams, {}),
            };
        }
        else {
            requestParams = {
                ad_type: adType,
                output: 'vast',
                iu: adUnitId,
                impl: 's',
                gdfp_req: '1',
                plcmt: '1',
                env: 'vp',
                unviewed_position_start: '1',
                videoad_skippable: '0',
                sz: canAttemptVideo ? videoAdUnitSizes : adPixelUnitSize,
                ciu_szs: '728x90,300x250,300x600',
                correlator: Date.now(),
                cust_params: custParams,
                ppid: getState(State.ppid),
                ...getState(State.dfpParams, {}),
            };
        }
        if (gdprApplies) {
            requestParams[DFP_GDPR_NON_PERSONALIZED_ADS_PARAMETER] = getState(State.canShowPersonalizedAds)
                ? 0
                : 1;
        }
        requestParams.gdpr = gdprApplies ? 1 : 0;
        requestParams.vpmute = volume > 0 ? 1 : 0;
        if (usPrivacyString) {
            requestParams.us_privacy = usPrivacyString;
        }
        if (isUserInUS) {
            requestParams[US_RESTRICTED_DATA_PROCESSING_ADS_PARAMETER] =
                isOptedOutOfTargeting ? 1 : 0;
        }
        if (gdprApplies && tcString) {
            requestParams.gdpr_consent = tcString;
        }
        requestParams.isFirstInSession = isFirstInSession;
        dfpUrl.query = filterFalsyValues(requestParams);
        return url.format(dfpUrl);
    },
};
export default networkUrlBuilder;
